import {
  Award,
  Calendar,
  Clock,
  HelpCircle,
  Mail,
  MapPin,
  MessageCircle,
  Star,
  TrendingUp,
  User,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function MentorStatic() {
  const mentorStats = [
    {
      label: "Sessions Completed",
      value: "23",
      icon: Clock,
      trend: "+3 this month",
    },
    {
      label: "Average Rating",
      value: "4.9",
      icon: Star,
      trend: "Excellent feedback",
    },
    {
      label: "Success Rate",
      value: "94%",
      icon: TrendingUp,
      trend: "Goal achievement",
    },
    {
      label: "Experience",
      value: "8+ years",
      icon: Award,
      trend: "Sales leadership",
    },
  ];

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        <h2>Your Mentor</h2>
        <p className="text-muted-foreground">
          Connect with your dedicated sales mentor and track your progress
          together.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle
                className="flex items-center gap-2"
                style={{ color: "#5ac0fa" }}
              >
                <User className="size-5" />
                Mentor Profile
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-6">
                <div className="size-24 overflow-hidden rounded-full border-2 border-primary/20 bg-primary/10">
                  <img
                    src={"/60695538-3ffa-4211-9645-68b0244a31fe.png"}
                    alt="Jay Curran"
                    className="scale-130 size-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3>Jay Curran</h3>
                  <p className="mb-2" style={{ color: "#5ac0fa" }}>
                    Sales Director, Oracle
                  </p>
                  <p className="mb-4 text-muted-foreground">
                    Experienced sales leader with over 8 years in enterprise
                    software sales. Specializes in helping sales professionals
                    overcome challenges, improve performance, and advance their
                    careers. Expert in consultative selling, team leadership,
                    and client relationship management.
                  </p>

                  <div className="mb-4 flex flex-wrap gap-2">
                    <Badge variant="secondary">Enterprise Sales</Badge>
                    <Badge variant="secondary">Team Leadership</Badge>
                    <Badge variant="secondary">Client Relations</Badge>
                    <Badge variant="secondary">Performance Coaching</Badge>
                  </div>

                  <div className="flex items-center gap-4 text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <MapPin className="size-4" />
                      <span>San Francisco, CA</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Mail className="size-4" />
                      <span>Available via chat</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle
                className="flex items-center gap-2"
                style={{ color: "#5ac0fa" }}
              >
                <MessageCircle className="size-5" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Button
                className="flex items-center gap-2"
                style={{ backgroundColor: "#5ac0fa" }}
              >
                <MessageCircle className="size-4" />
                Start New Chat
              </Button>
              <Button
                variant="outline"
                className="flex items-center gap-2 border-primary/20 hover:bg-primary/5"
              >
                <Calendar className="size-4" />
                Schedule Session
              </Button>
              <Button
                variant="outline"
                className="flex items-center gap-2 border-primary/20 hover:bg-primary/5"
              >
                <HelpCircle className="size-4" />
                Request Support
              </Button>
              <Button
                variant="outline"
                className="flex items-center gap-2 border-primary/20 hover:bg-primary/5"
              >
                <Star className="size-4" />
                Leave Feedback
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Stats Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle
                className="flex items-center gap-2"
                style={{ color: "#5ac0fa" }}
              >
                <TrendingUp className="size-5" />
                Mentorship Stats
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {mentorStats.map((stat, index) => (
                <div key={index} className="border-l-4 border-primary/30 pl-4">
                  <div className="mb-1 flex items-center gap-2">
                    <stat.icon
                      className="size-4"
                      style={{ color: "#5ac0fa" }}
                    />
                    <span className="text-muted-foreground">{stat.label}</span>
                  </div>
                  <div
                    className="mb-1 text-2xl font-medium"
                    style={{ color: "#5ac0fa" }}
                  >
                    {stat.value}
                  </div>
                  <p className="text-xs text-muted-foreground">{stat.trend}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle
                className="flex items-center gap-2"
                style={{ color: "#5ac0fa" }}
              >
                <Clock className="size-5" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-sm">
                <div className="mb-1 flex items-center justify-between">
                  <span>Last session</span>
                  <span className="text-muted-foreground">2 days ago</span>
                </div>
                <p className="text-muted-foreground">
                  Discussed pipeline management strategies
                </p>
              </div>
              <div className="text-sm">
                <div className="mb-1 flex items-center justify-between">
                  <span>Resource shared</span>
                  <span className="text-muted-foreground">1 week ago</span>
                </div>
                <p className="text-muted-foreground">
                  Sales objection handling guide
                </p>
              </div>
              <div className="text-sm">
                <div className="mb-1 flex items-center justify-between">
                  <span>Goal achieved</span>
                  <span className="text-muted-foreground">2 weeks ago</span>
                </div>
                <p className="text-muted-foreground">
                  Exceeded quarterly target by 15%
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
